import type { EventProperties } from '@segment/analytics-next';
import { TZDate } from '@date-fns/tz';
import { addDays, isMatch, isValid } from 'date-fns';
import { getCookieRootDomain } from '@/utils/config';

import {
  getCookie,
  setExpiringCookie,
  setExpiringCookieUntil,
  setExpiringCookieWithDomain,
  setExpiringCookieWithDomainUntil
} from '@/utils/cookies';

import { getForcedAbTestGroup } from './ab-testing-forced-enable';

export type AbTestGroup = 'control' | 'variant';

export interface ExperimentConfig {
  readonly key: string;
  readonly displayName: string;
  readonly featureEnabledForGroup: AbTestGroup;
  readonly isActive: boolean;
  readonly startDate?: string;
  readonly startTime?: string;
  readonly timeZone?: string;
  readonly daysActive?: number;
}

/** Registry shared by assignment cookies, Segment properties and exposure tracking. */
export const experimentConfigs: readonly ExperimentConfig[] = [];

const experimentDateFormat = 'yyyy-MM-dd';
const experimentTimeFormat = 'HH:mm';
const defaultExperimentTimeZone = 'Europe/Tallinn';

function getExperimentStartDate(config: ExperimentConfig): Date | null {
  if (
    config.startDate === undefined ||
    config.startTime === undefined ||
    !isMatch(config.startDate, experimentDateFormat) ||
    !isMatch(config.startTime, experimentTimeFormat)
  ) {
    return null;
  }

  const [year, month, day] = config.startDate.split('-').map(Number);
  const [hours, minutes] = config.startTime.split(':').map(Number);

  if (
    year === undefined ||
    month === undefined ||
    day === undefined ||
    hours === undefined ||
    minutes === undefined
  ) {
    return null;
  }

  try {
    const startDate = new TZDate(
      year,
      month - 1,
      day,
      hours,
      minutes,
      config.timeZone ?? defaultExperimentTimeZone
    );

    return isValid(startDate) ? startDate : null;
  } catch {
    return null;
  }
}

export function getExperimentEndDate(config: ExperimentConfig): Date | null {
  if (
    config.daysActive === undefined ||
    !Number.isFinite(config.daysActive) ||
    !Number.isInteger(config.daysActive) ||
    config.daysActive <= 0
  ) {
    return null;
  }

  const startDate = getExperimentStartDate(config);

  if (startDate === null) {
    return null;
  }

  return addDays(startDate, config.daysActive);
}

export function isExperimentWithinActiveWindow(
  config: ExperimentConfig,
  currentDate = new Date()
): boolean {
  if (!config.isActive) {
    return false;
  }

  const startDate = getExperimentStartDate(config);
  const endDate = getExperimentEndDate(config);

  if (startDate === null || endDate === null) {
    return false;
  }

  const currentTimestamp = currentDate.getTime();

  return currentTimestamp >= startDate.getTime() && currentTimestamp < endDate.getTime();
}

function findExperimentConfig(experimentKey: string): ExperimentConfig | undefined {
  return experimentConfigs.find(configuration => configuration.key === experimentKey);
}

/**
 * Cookie name for “`Experiment Exposed` already sent” dedupe (the value is a flag — the exposed
 * variant comes from the allocation, not from this cookie). On production, parent-domain `.bobw.co`
 * shares across subdomains; on `preview.bobw.co` we also mirror to `.staging-bobw.com` for booking staging.
 */
function toExposureSentCookieName(experimentKey: string): string {
  return `${experimentKey}_exposed`;
}

const previewHostname = 'preview.bobw.co';
const stagingBobwMirrorCookieDomain = '.staging-bobw.com';

function setAbTestExpiringCookie(name: string, value: string, days?: number): void {
  setExpiringCookie(name, value, days);

  if (getCookieRootDomain() === previewHostname) {
    setExpiringCookieWithDomain({
      name,
      value,
      days,
      domain: stagingBobwMirrorCookieDomain
    });
  }
}

function setAbTestExpiringCookieUntil(name: string, value: string, expirationDate: Date): void {
  setExpiringCookieUntil(name, value, expirationDate);

  if (getCookieRootDomain() === previewHostname) {
    setExpiringCookieWithDomainUntil({
      name,
      value,
      expirationDate,
      domain: stagingBobwMirrorCookieDomain
    });
  }
}

/** Cookie value when exposure was recorded (boolean string; avoids duplicate `Experiment Exposed`). */
const exposureSentCookieValue = 'true';

function toAllocatedPropertyKey(experimentKey: string): string {
  return `${experimentKey}_allocated`;
}

function toExposedPropertyKey(experimentKey: string): string {
  return `${experimentKey}_exposed`;
}

export function getOrAssignTestGroup(config: ExperimentConfig): AbTestGroup {
  const forcedGroup = getForcedAbTestGroup(config.key);

  if (forcedGroup !== null) {
    return forcedGroup;
  }

  const newGroup: AbTestGroup = Math.random() < 0.5 ? 'control' : 'variant';

  try {
    const storageKey = config.key;
    const storedGroup = getCookie(storageKey);

    if (storedGroup === 'control' || storedGroup === 'variant') {
      return storedGroup;
    }

    const experimentEndDate = getExperimentEndDate(config);

    if (experimentEndDate !== null) {
      setAbTestExpiringCookieUntil(storageKey, newGroup, experimentEndDate);
    }

    return newGroup;
  } catch {
    return newGroup;
  }
}

/**
 * Group the experiment behaves as for this browser, `null` when the experiment is off.
 * A non-production forced group (debug widget) makes an inactive experiment behave as
 * active — feature gating, Segment superproperties, and `Experiment Exposed` all follow it —
 * so the full tracking flow can be verified on stage/local. Without a forced group,
 * inactive experiments stay `null` and produce no events.
 */
export function getAbTestGroup(experimentKey: string): AbTestGroup | null {
  const config = findExperimentConfig(experimentKey);

  if (config === undefined) {
    return null;
  }

  const forcedGroup = getForcedAbTestGroup(experimentKey);

  if (forcedGroup !== null) {
    return forcedGroup;
  }

  if (!isExperimentWithinActiveWindow(config)) {
    return null;
  }

  return getOrAssignTestGroup(config);
}

/** Gate for experiment feature UI: the effective group matches `featureEnabledForGroup`. */
export function isExperimentFeatureEnabled(experimentKey: string): boolean {
  const config = findExperimentConfig(experimentKey);

  if (config === undefined) {
    return false;
  }

  return getAbTestGroup(experimentKey) === config.featureEnabledForGroup;
}

/** Whether `Experiment Exposed` was already recorded for this browser (parent-domain cookie). */
export function hasExperimentExposureSent(experimentKey: string): boolean {
  return getCookie(toExposureSentCookieName(experimentKey)) === exposureSentCookieValue;
}

export function markExperimentExposureSent(experimentKey: string): void {
  const config = findExperimentConfig(experimentKey);

  if (config === undefined || getForcedAbTestGroup(experimentKey) !== null) {
    setAbTestExpiringCookie(
      toExposureSentCookieName(experimentKey),
      exposureSentCookieValue,
      config?.daysActive
    );

    return;
  }

  const experimentEndDate = getExperimentEndDate(config);

  if (experimentEndDate !== null) {
    setAbTestExpiringCookieUntil(
      toExposureSentCookieName(experimentKey),
      exposureSentCookieValue,
      experimentEndDate
    );
  }
}

function getExperimentSegmentProperties(experimentKey: string): EventProperties {
  const allocated = getAbTestGroup(experimentKey);
  const result: EventProperties = {};

  if (allocated !== null) {
    result[toAllocatedPropertyKey(experimentKey)] = allocated;

    if (hasExperimentExposureSent(experimentKey)) {
      result[toExposedPropertyKey(experimentKey)] = allocated;
    }
  }

  return result;
}

/** Allocation/exposure superproperties for every registered experiment (only active ones produce keys). */
export function getExperimentsSegmentProperties(): EventProperties {
  const result: EventProperties = {};

  for (const config of experimentConfigs) {
    Object.assign(result, getExperimentSegmentProperties(config.key));
  }

  return result;
}

/**
 * Ensures assignment cookies exist on first load for active experiments (before first Segment call).
 */
export function bootstrapExperimentCookies(): void {
  for (const config of experimentConfigs) {
    if (isExperimentWithinActiveWindow(config)) {
      void getOrAssignTestGroup(config);
    }
  }
}
